import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { imageUrl } from '../lib/images';

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="Om MC Transport | Familiedrevet vognmand i Danmark"
        description="Lær mere om MC Transport og vores værdier. En familiedrevet transportvirksomhed med 24/7 døgnservice, høj pålidelighed og stor vognpark."
      />

      {/* Hero */}
      <section className="bg-zinc-950 text-white py-16 lg:py-24 border-b border-zinc-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(220,38,38,0.2),transparent_70%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-red-500 font-condensed mb-2 block">
            Familiedrevet Vognmandsforretning Siden Starten
          </span>
          <h1 className="font-condensed text-4xl sm:text-6xl font-extrabold uppercase tracking-tight text-white">
            Om MC Transport
          </h1>
          <p className="mt-4 text-base sm:text-xl text-zinc-300 max-w-2xl mx-auto">
            Her kender alle hinanden, og hjælpsomhed, respekt og ordentlighed er en naturlig del af hverdagen.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Martin's Image */}
            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-zinc-200 bg-zinc-900">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/7ab2fd9d-1ad5-42a7-a15d-22229bea7818/martin-indehaver-mc-transport-kontor.jpg', 'width=800,quality=90,format=auto')}
                  alt="Martin på kontoret hos MC Transport"
                  className="w-full h-auto object-cover"
                />
                <div className="p-6 bg-zinc-950 text-white border-t border-zinc-800">
                  <h3 className="font-condensed text-2xl font-bold uppercase text-white">
                    Martin
                  </h3>
                  <p className="text-xs text-red-500 font-semibold uppercase tracking-wider">
                    Indehaver & Chef · MC Transport
                  </p>
                  <p className="text-xs text-zinc-400 mt-2">
                    &quot;Vi går op i sammenhold og hygge samtidig med at vi forventer, at der sker noget, når vi er på arbejde.&quot;
                  </p>
                </div>
              </div>
            </div>

            {/* Story */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-bold uppercase tracking-wider text-red-600 font-condensed">
                Historien & Menneskene
              </span>
              <h2 className="font-condensed text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-zinc-950 leading-tight">
                Ikke bare et nummer – en del af holdet
              </h2>

              <div className="space-y-4 text-zinc-700 text-sm sm:text-base leading-relaxed">
                <p>
                  <Link to="/" className="font-semibold text-zinc-950 hover:text-red-600 underline decoration-red-600/40 underline-offset-2 transition-colors">MC Transport</Link> er et transportselskab, der kører <Link to="/ydelser/autotransport-ydelser" className="text-red-600 font-medium hover:text-red-700 underline decoration-red-600/30 underline-offset-2 hover:decoration-red-600 transition-colors">autotransport</Link>, <Link to="/autohjaelp" className="text-red-600 font-medium hover:text-red-700 underline decoration-red-600/30 underline-offset-2 hover:decoration-red-600 transition-colors">autohjælp 24/7</Link>, <Link to="/bjaergning" className="text-red-600 font-medium hover:text-red-700 underline decoration-red-600/30 underline-offset-2 hover:decoration-red-600 transition-colors">bjærgning</Link> og <Link to="/ydelser/godstransport" className="text-red-600 font-medium hover:text-red-700 underline decoration-red-600/30 underline-offset-2 hover:decoration-red-600 transition-colors">godstransport</Link> på Sjælland, Fyn, Jylland og i resten af Europa. Vi har et tæt samarbejde med forsikringsselskaber, Falck, autoforhandlere, værksteder, autoophuggere og private bilister.
                </p>
                <p>
                  Vi er en familieejet virksomhed med hovedsæde og <Link to="/kontakt" className="text-zinc-900 font-medium hover:text-red-600 underline decoration-zinc-300 underline-offset-2 transition-colors">sikret plads i Ugerløse</Link> ved Holbæk samt afdelinger i Odense og Hedensted. Martin er chefen, Pia (Martins bedre halvdel) sidder med regnskab og lønninger i tæt samarbejde med vores disponent og kontoransatte. Familiens 3 børn er også at finde på kontoret i tide og utide – til stor glæde for hele holdet af chauffører og medarbejdere.
                </p>
                <p>
                  Vores mål er at være en arbejdsplads og en samarbejdspartner, du med stolthed vil anbefale til dem, der betyder noget for dig. Vi tror på, at gode resultater skabes gennem godt samarbejde, gensidig respekt og ordentlige arbejdsforhold. Fra 1. november 2026 udvider vi desuden med et komplet <Link to="/daekcenter" className="text-red-600 font-medium hover:text-red-700 underline decoration-red-600/30 underline-offset-2 hover:decoration-red-600 transition-colors">dækcenter</Link> på pladsen i Hedensted.
                </p>
              </div>

              {/* Stats badges */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4">
                <div className="p-4 bg-zinc-50 rounded-lg border border-zinc-200">
                  <div className="font-condensed text-3xl font-extrabold text-red-600">24/7/365</div>
                  <div className="text-xs text-zinc-600 font-medium mt-1">Døgnåben Vagtcentral</div>
                </div>
                <div className="p-4 bg-zinc-50 rounded-lg border border-zinc-200">
                  <div className="font-condensed text-3xl font-extrabold text-zinc-950">80.000 m²</div>
                  <div className="text-xs text-zinc-600 font-medium mt-1">Sikret Plads i Ugerløse</div>
                </div>
                <div className="p-4 bg-zinc-50 rounded-lg border border-zinc-200 col-span-2 sm:col-span-1">
                  <div className="font-condensed text-3xl font-extrabold text-zinc-950">64 Kameraer</div>
                  <div className="text-xs text-zinc-600 font-medium mt-1">HD & Infrarød Overvågning</div>
                </div>
              </div>

              <div className="pt-4 flex flex-wrap items-center gap-4">
                <Link
                  to="/chauffoer-soeges"
                  className="px-6 py-3.5 bg-red-600 hover:bg-red-700 text-white font-condensed text-lg font-bold uppercase rounded shadow"
                >
                  Se ledige job
                </Link>
                <Link
                  to="/kontakt"
                  className="px-6 py-3.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-900 font-condensed text-lg font-bold uppercase rounded"
                >
                  Kontakt kontoret
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities and fleet section */}
      <section className="py-16 bg-zinc-100 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-red-600 font-condensed">
              Materiel & Faciliteter
            </span>
            <h2 className="font-condensed text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-zinc-950 mt-1">
              Moderne rød vognpark til enhver udfordring
            </h2>
            <p className="text-sm text-zinc-600 mt-2">
              Vi råder over skydeladsbiler, kranbiler, 8-bils transportere, bjergningsbiler, maskintransport, 4-akslet Scania R660 bjærger, 6 Uni Man 4x4, sengetrailer og gardintrailer med medbringertruck.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/ydelser/autotransport-ydelser" className="block bg-white rounded-lg p-5 border border-zinc-200 shadow-sm hover:border-red-600 transition-colors group">
              <i className="fa-solid fa-truck-ramp-box text-2xl text-red-600 mb-3 group-hover:scale-110 transition-transform" aria-hidden="true" />
              <h4 className="font-condensed text-xl font-bold uppercase text-zinc-900 group-hover:text-red-600 transition-colors">Autotransportere</h4>
              <p className="text-xs text-zinc-600 mt-1">
                Transportere til op til 8 biler ad gangen samt specialbyggede biler til skadede og brændte køretøjer.
              </p>
            </Link>

            <Link to="/bjaergning" className="block bg-white rounded-lg p-5 border border-zinc-200 shadow-sm hover:border-red-600 transition-colors group">
              <i className="fa-solid fa-truck-pickup text-2xl text-red-600 mb-3 group-hover:scale-110 transition-transform" aria-hidden="true" />
              <h4 className="font-condensed text-xl font-bold uppercase text-zinc-900 group-hover:text-red-600 transition-colors">Bjærgning & 4x4</h4>
              <p className="text-xs text-zinc-600 mt-1">
                4-akslet Scania R660 bjærger til busser og lastbiler, samt 6 Uni Man 4x4 til terrænkørsel og fritrækning.
              </p>
            </Link>

            <Link to="/ydelser/krantransport" className="block bg-white rounded-lg p-5 border border-zinc-200 shadow-sm hover:border-red-600 transition-colors group">
              <i className="fa-solid fa-industry text-2xl text-red-600 mb-3 group-hover:scale-110 transition-transform" aria-hidden="true" />
              <h4 className="font-condensed text-xl font-bold uppercase text-zinc-900 group-hover:text-red-600 transition-colors">Kran & Sengetrailer</h4>
              <p className="text-xs text-zinc-600 mt-1">
                28 tm kranbil til tunge løft samt sænketrailer til transport af store maskiner, entreprenørmateriel og både.
              </p>
            </Link>

            <Link to="/kontakt" className="block bg-white rounded-lg p-5 border border-zinc-200 shadow-sm hover:border-red-600 transition-colors group">
              <i className="fa-solid fa-shield-halved text-2xl text-red-600 mb-3 group-hover:scale-110 transition-transform" aria-hidden="true" />
              <h4 className="font-condensed text-xl font-bold uppercase text-zinc-900 group-hover:text-red-600 transition-colors">Sikret Plads</h4>
              <p className="text-xs text-zinc-600 mt-1">
                80.000 m² indhegnet plads i Ugerløse med 64 overvågningskameraer, automatiske porte og 24/7 omlæsningssluse.
              </p>
            </Link>
          </div>

          {/* Vognpark billeder fra /images/ */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            <div className="rounded-xl overflow-hidden shadow-md border border-zinc-200 bg-zinc-900">
              <img
                src={imageUrl('/_assets/volvo-autotransporter-flerbils-transport.jpg', 'width=600,height=380,fit=cover,quality=85,format=auto')}
                alt="Rød Volvo autotransporter med biler"
                className="w-full h-56 object-cover"
              />
              <div className="p-4 bg-white border-t border-zinc-100">
                <span className="text-xs font-bold uppercase text-red-600 font-condensed">Autotransport</span>
                <p className="text-xs text-zinc-600 font-medium mt-0.5">8-bils transportere i fast rutefart</p>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden shadow-md border border-zinc-200 bg-zinc-900">
              <img
                src={imageUrl('/_assets/volvo-bjaergningsvogn-motorvej-assistance.png', 'width=600,height=380,fit=cover,quality=85,format=auto')}
                alt="Rød Volvo bjærgningsvogn fra MC Transport"
                className="w-full h-56 object-cover"
              />
              <div className="p-4 bg-white border-t border-zinc-100">
                <span className="text-xs font-bold uppercase text-red-600 font-condensed">Tungvognsbjærgning</span>
                <p className="text-xs text-zinc-600 font-medium mt-0.5">24/7 døgnberedskab på motorveje og landeveje</p>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden shadow-md border border-zinc-200 bg-zinc-900">
              <img
                src={imageUrl('/_assets/scania-lastbil-blokvogn-transport.jpg', 'width=600,height=380,fit=cover,quality=85,format=auto')}
                alt="Rød Scania lastbil med blokvogn"
                className="w-full h-56 object-cover"
              />
              <div className="p-4 bg-white border-t border-zinc-100">
                <span className="text-xs font-bold uppercase text-red-600 font-condensed">Specialtransport & Blokvogn</span>
                <p className="text-xs text-zinc-600 font-medium mt-0.5">Erfarne chauffører og certificeret grej</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
